// ===========================================
// BUSTED! BLACKJACK - PIXI.JS INSURANCE PANEL
// ===========================================
// Pure PixiJS implementation of the insurance modal.
// Centered on table canvas, replaces React InsuranceToggle.
// ===========================================

import { Container, Graphics, Text, TextStyle, FederatedPointerEvent, Ticker, Rectangle } from 'pixi.js';

// ===========================================
// TYPES
// ===========================================

export interface PixiInsurancePanelConfig {
  /** Callback when YES is clicked */
  onAccept: (amount: number) => void;
  /** Callback when NO is clicked */
  onDecline: () => void;
  /** Canvas dimensions for centering */
  canvasWidth: number;
  canvasHeight: number;
}

interface ButtonState {
  graphics: Graphics;
  text: Text;
  enabled: boolean;
  hovered: boolean;
}

// ===========================================
// CONSTANTS
// ===========================================

const PANEL_WIDTH = 300;
const PANEL_HEIGHT = 180;
const PANEL_RADIUS = 16;
const PANEL_BG_COLOR = 0x1a1a2e;
const PANEL_BG_ALPHA = 0.95;
const PANEL_BORDER_COLOR = 0xFFD700; // Gold
const PANEL_BORDER_WIDTH = 2;

const BUTTON_WIDTH = 100;
const BUTTON_HEIGHT = 48;
const BUTTON_RADIUS = 8;
const BUTTON_GAP = 20;

const YES_COLOR = 0x22c55e; // Green
const YES_HOVER_COLOR = 0x16a34a;
const NO_COLOR = 0xef4444; // Red
const NO_HOVER_COLOR = 0xdc2626;
const DISABLED_COLOR = 0x4a4a4a;

// Neon border animation (matching PixiControlPanel style)
const NEON_BORDER_WIDTH = 4;
const NEON_GLOW_ALPHA = 0.8;
const NEON_ANIMATION_SPEED = 0.01; // Diagonal flow speed (matching control panel)

// Rainbow colors for neon border animation (matching control panel gradient)
const NEON_RAINBOW_COLORS = [
  0xff0080, // pink
  0xff00ff, // magenta
  0x8000ff, // purple
  0x0080ff, // blue
  0x00ffff, // cyan
  0x00ff80, // green-cyan
  0x00ff00, // green
  0x80ff00, // yellow-green
  0xffff00, // yellow
  0xff8000, // orange
];

// Countdown colors (matching InsuranceToggle)
const COUNTDOWN_GREEN = 0x22c55e;
const COUNTDOWN_YELLOW = 0xeab308;
const COUNTDOWN_RED = 0xef4444;

// ===========================================
// PIXI INSURANCE PANEL CLASS
// ===========================================

export class PixiInsurancePanel extends Container {
  private config: PixiInsurancePanelConfig;
  private background: Graphics;
  private titleText: Text;
  private costText: Text;
  private yesButton: ButtonState;
  private noButton: ButtonState;
  private countdownArc: Graphics;
  private recommendationBorder: Graphics | null = null;

  // State
  private insuranceCost: number = 0;
  private canAfford: boolean = true;
  private recommendation: 'ACCEPT' | 'DECLINE' | null = null;
  private countdownDeadline: number | null = null;
  private countdownTotal: number = 10000; // 10 seconds visible countdown
  private isVisible: boolean = false;

  // Animation
  private ticker: Ticker | null = null;
  private neonPulsePhase: number = 0; // For pulsing neon border animation

  constructor(config: PixiInsurancePanelConfig) {
    super();
    this.config = config;
    this.eventMode = 'static';
    this.visible = false;

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create title
    this.titleText = new Text({
      text: 'INSURANCE?',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 28,
        fontWeight: 'bold',
        fill: 0xffffff,
      }),
    });
    this.titleText.anchor.set(0.5, 0);
    this.addChild(this.titleText);

    // Create cost text
    this.costText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 18,
        fill: 0xcccccc,
      }),
    });
    this.costText.anchor.set(0.5, 0);
    this.addChild(this.costText);

    // Create countdown arc
    this.countdownArc = new Graphics();
    this.addChild(this.countdownArc);

    // Create buttons
    this.yesButton = this.createButton('YES', YES_COLOR);
    this.noButton = this.createButton('NO', NO_COLOR);

    // Build initial layout
    this.buildLayout();

    // Start ticker for countdown and neon border animation
    this.ticker = new Ticker();
    this.ticker.add(this.updateAnimations.bind(this));
    this.ticker.start();
  }

  private createButton(label: string, color: number): ButtonState {
    const graphics = new Graphics();
    graphics.eventMode = 'static';
    graphics.cursor = 'pointer';
    // Set explicit hit area to ensure entire button is clickable
    graphics.hitArea = new Rectangle(0, 0, BUTTON_WIDTH, BUTTON_HEIGHT);
    this.addChild(graphics);

    const text = new Text({
      text: label,
      style: new TextStyle({
        fontFamily: 'Arial, sans-serif',
        fontSize: 20,
        fontWeight: 'bold',
        fill: 0xffffff,
      }),
    });
    text.anchor.set(0.5, 0.5);
    text.eventMode = 'none'; // Don't intercept clicks - let button graphics handle them
    this.addChild(text);

    const state: ButtonState = {
      graphics,
      text,
      enabled: true,
      hovered: false,
    };

    // Event handlers
    graphics.on('pointerover', () => {
      if (state.enabled) {
        state.hovered = true;
        this.drawButton(state, color);
      }
    });

    graphics.on('pointerout', () => {
      state.hovered = false;
      this.drawButton(state, color);
    });

    graphics.on('pointerdown', () => {
      if (state.enabled) {
        if (label === 'YES') {
          console.log('[PixiInsurancePanel] YES clicked, cost:', this.insuranceCost);
          this.config.onAccept(this.insuranceCost);
        } else {
          console.log('[PixiInsurancePanel] NO clicked');
          this.config.onDecline();
        }
      }
    });

    return state;
  }

  private drawButton(state: ButtonState, baseColor: number): void {
    const g = state.graphics;
    g.clear();

    let color = baseColor;
    if (!state.enabled) {
      color = DISABLED_COLOR;
    } else if (state.hovered) {
      color = baseColor === YES_COLOR ? YES_HOVER_COLOR : NO_HOVER_COLOR;
    }

    g.roundRect(0, 0, BUTTON_WIDTH, BUTTON_HEIGHT, BUTTON_RADIUS);
    g.fill({ color, alpha: 1 });

    // Update cursor
    g.cursor = state.enabled ? 'pointer' : 'default';
  }

  private buildLayout(): void {
    // Draw background
    this.background.clear();
    this.background.roundRect(0, 0, PANEL_WIDTH, PANEL_HEIGHT, PANEL_RADIUS);
    this.background.fill({ color: PANEL_BG_COLOR, alpha: PANEL_BG_ALPHA });
    this.background.stroke({ color: PANEL_BORDER_COLOR, width: PANEL_BORDER_WIDTH });

    // Position title
    this.titleText.x = PANEL_WIDTH / 2;
    this.titleText.y = 20;

    // Position cost text
    this.costText.x = PANEL_WIDTH / 2;
    this.costText.y = 55;

    // Position countdown arc (top right)
    this.countdownArc.x = PANEL_WIDTH - 35;
    this.countdownArc.y = 35;

    // Position buttons
    const buttonsY = PANEL_HEIGHT - BUTTON_HEIGHT - 25;
    const totalButtonsWidth = BUTTON_WIDTH * 2 + BUTTON_GAP;
    const buttonsStartX = (PANEL_WIDTH - totalButtonsWidth) / 2;

    // YES button
    this.yesButton.graphics.x = buttonsStartX;
    this.yesButton.graphics.y = buttonsY;
    this.yesButton.text.x = buttonsStartX + BUTTON_WIDTH / 2;
    this.yesButton.text.y = buttonsY + BUTTON_HEIGHT / 2;
    this.drawButton(this.yesButton, YES_COLOR);

    // NO button
    this.noButton.graphics.x = buttonsStartX + BUTTON_WIDTH + BUTTON_GAP;
    this.noButton.graphics.y = buttonsY;
    this.noButton.text.x = buttonsStartX + BUTTON_WIDTH + BUTTON_GAP + BUTTON_WIDTH / 2;
    this.noButton.text.y = buttonsY + BUTTON_HEIGHT / 2;
    this.drawButton(this.noButton, NO_COLOR);

    // Center panel on canvas
    this.updatePosition();
  }

  /**
   * Update position to center on canvas
   */
  public updatePosition(): void {
    this.x = (this.config.canvasWidth - PANEL_WIDTH) / 2;
    this.y = (this.config.canvasHeight - PANEL_HEIGHT) / 2;
  }

  /**
   * Update canvas dimensions (call when canvas resizes)
   */
  public updateConfig(config: Partial<PixiInsurancePanelConfig>): void {
    Object.assign(this.config, config);
    this.updatePosition();
  }

  /**
   * Show the insurance panel
   */
  public show(cost: number, deadline: number | null): void {
    console.log('[PixiInsurancePanel] show() cost:', cost, 'deadline:', deadline);
    this.insuranceCost = cost;
    this.countdownDeadline = deadline;
    this.isVisible = true;
    this.visible = true;

    // Update cost text
    this.costText.text = `Cost: $${cost.toLocaleString()}`;

    // Reset button states
    this.yesButton.enabled = this.canAfford;
    this.noButton.enabled = true;
    this.drawButton(this.yesButton, YES_COLOR);
    this.drawButton(this.noButton, NO_COLOR);

    // Update recommendation highlight
    this.updateRecommendationBorder();
  }

  /**
   * Hide the insurance panel
   */
  public hide(): void {
    console.log('[PixiInsurancePanel] hide()');
    this.isVisible = false;
    this.visible = false;
    this.countdownDeadline = null;
  }

  /**
   * Set whether player can afford insurance
   */
  public setCanAfford(canAfford: boolean): void {
    this.canAfford = canAfford;
    this.yesButton.enabled = canAfford;
    this.drawButton(this.yesButton, YES_COLOR);
  }

  /**
   * Set the recommended action (highlights the button)
   */
  public setRecommendation(rec: 'ACCEPT' | 'DECLINE' | null): void {
    console.log('[PixiInsurancePanel] setRecommendation called:', rec);
    this.recommendation = rec;
    this.updateRecommendationBorder();
  }

  private updateRecommendationBorder(): void {
    console.log('[PixiInsurancePanel] updateRecommendationBorder:', {
      recommendation: this.recommendation,
      isVisible: this.isVisible,
    });

    // Remove existing border
    if (this.recommendationBorder) {
      this.removeChild(this.recommendationBorder);
      this.recommendationBorder.destroy();
      this.recommendationBorder = null;
    }

    if (!this.recommendation || !this.isVisible) {
      console.log('[PixiInsurancePanel] Not drawing border - recommendation or visibility check failed');
      return;
    }

    console.log('[PixiInsurancePanel] Creating animated neon border for', this.recommendation);

    // Create neon glow border container (will be animated in drawNeonBorder)
    // Insert at index 4 (after background, title, cost, countdown but BEFORE buttons)
    // so the animated border renders BEHIND the clickable buttons
    this.recommendationBorder = new Graphics();
    this.addChildAt(this.recommendationBorder, 4);

    // Reset pulse phase for fresh animation
    this.neonPulsePhase = 0;

    // Initial draw
    this.drawNeonBorder();

    console.log('[PixiInsurancePanel] Animated neon border added to container');
  }

  /**
   * Update all animations (called every frame)
   */
  private updateAnimations(ticker: Ticker): void {
    // Safety check - stop if panel has been destroyed
    if (this.destroyed || !this.background || this.background.destroyed) {
      // Stop the ticker if it's still running
      if (this.ticker) {
        this.ticker.stop();
      }
      return;
    }

    // Update neon animation phase (for diagonal flow effect)
    // NOTE: Add fixed increment per frame to match PixiControlPanel speed (not deltaMS-based)
    this.neonPulsePhase += NEON_ANIMATION_SPEED;
    if (this.neonPulsePhase > 1) this.neonPulsePhase -= 1;

    // Redraw neon border with gradient animation
    this.drawNeonBorder();

    // Update countdown
    this.updateCountdown();
  }

  /**
   * Draw animated rainbow gradient neon border around recommended button.
   * Uses diagonal flowing gradient matching PixiControlPanel style.
   */
  private drawNeonBorder(): void {
    // Skip if no recommendation or not visible
    if (!this.recommendation || !this.isVisible || !this.recommendationBorder) {
      return;
    }

    // Determine which button to highlight
    const targetButton = this.recommendation === 'ACCEPT' ? this.yesButton : this.noButton;
    const buttonX = targetButton.graphics.x;
    const buttonY = targetButton.graphics.y;

    // Clear and redraw
    this.recommendationBorder.clear();

    // Calculate border dimensions
    const borderOffset = NEON_BORDER_WIDTH * 0.5;
    const left = buttonX - borderOffset;
    const top = buttonY - borderOffset;
    const width = BUTTON_WIDTH + NEON_BORDER_WIDTH;
    const height = BUTTON_HEIGHT + NEON_BORDER_WIDTH;
    const radius = BUTTON_RADIUS + borderOffset;

    // For 45-degree gradient, color is based on (x + y) position
    const diagonalLength = width + height;
    const numColors = NEON_RAINBOW_COLORS.length;
    const numSegments = 30; // Segments per edge for smooth gradient

    // Helper to interpolate between two colors
    const lerpColor = (c1: number, c2: number, t: number): number => {
      const r1 = (c1 >> 16) & 0xff;
      const g1 = (c1 >> 8) & 0xff;
      const b1 = c1 & 0xff;
      const r2 = (c2 >> 16) & 0xff;
      const g2 = (c2 >> 8) & 0xff;
      const b2 = c2 & 0xff;
      const r = Math.round(r1 + (r2 - r1) * t);
      const g = Math.round(g1 + (g2 - g1) * t);
      const b = Math.round(b1 + (b2 - b1) * t);
      return (r << 16) | (g << 8) | b;
    };

    // Helper to get smoothly interpolated color at a diagonal position (0 to 1)
    const getColorAtDiagonal = (diagPos: number): number => {
      const adjustedPos = ((diagPos + this.neonPulsePhase) % 1 + 1) % 1;
      const scaledPos = adjustedPos * numColors;
      const index1 = Math.floor(scaledPos) % numColors;
      const index2 = (index1 + 1) % numColors;
      const t = scaledPos - Math.floor(scaledPos);
      return lerpColor(NEON_RAINBOW_COLORS[index1], NEON_RAINBOW_COLORS[index2], t);
    };

    // Helper to get diagonal position (0-1) from x,y coordinates
    const getDiagonalPos = (x: number, y: number): number => {
      return ((x - left) + (y - top)) / diagonalLength;
    };

    // TOP EDGE
    const topEdgeStart = left + radius;
    const topEdgeEnd = left + width - radius;
    const topEdgeLength = topEdgeEnd - topEdgeStart;
    const topSegmentWidth = topEdgeLength / numSegments;

    for (let i = 0; i < numSegments; i++) {
      const segStart = topEdgeStart + i * topSegmentWidth;
      const segEnd = segStart + topSegmentWidth;
      const midX = (segStart + segEnd) / 2;
      const color = getColorAtDiagonal(getDiagonalPos(midX, top));

      this.recommendationBorder.moveTo(segStart, top);
      this.recommendationBorder.lineTo(segEnd, top);
      this.recommendationBorder.stroke({ color, width: NEON_BORDER_WIDTH, alpha: NEON_GLOW_ALPHA });
    }

    // BOTTOM EDGE
    const bottomY = top + height;
    for (let i = 0; i < numSegments; i++) {
      const segStart = topEdgeStart + i * topSegmentWidth;
      const segEnd = segStart + topSegmentWidth;
      const midX = (segStart + segEnd) / 2;
      const color = getColorAtDiagonal(getDiagonalPos(midX, bottomY));

      this.recommendationBorder.moveTo(segStart, bottomY);
      this.recommendationBorder.lineTo(segEnd, bottomY);
      this.recommendationBorder.stroke({ color, width: NEON_BORDER_WIDTH, alpha: NEON_GLOW_ALPHA });
    }

    // LEFT EDGE
    const leftEdgeStart = top + radius;
    const leftEdgeEnd = top + height - radius;
    const leftEdgeLength = leftEdgeEnd - leftEdgeStart;
    const leftSegmentHeight = leftEdgeLength / numSegments;

    for (let i = 0; i < numSegments; i++) {
      const segStart = leftEdgeStart + i * leftSegmentHeight;
      const segEnd = segStart + leftSegmentHeight;
      const midY = (segStart + segEnd) / 2;
      const color = getColorAtDiagonal(getDiagonalPos(left, midY));

      this.recommendationBorder.moveTo(left, segStart);
      this.recommendationBorder.lineTo(left, segEnd);
      this.recommendationBorder.stroke({ color, width: NEON_BORDER_WIDTH, alpha: NEON_GLOW_ALPHA });
    }

    // RIGHT EDGE
    const rightX = left + width;
    for (let i = 0; i < numSegments; i++) {
      const segStart = leftEdgeStart + i * leftSegmentHeight;
      const segEnd = segStart + leftSegmentHeight;
      const midY = (segStart + segEnd) / 2;
      const color = getColorAtDiagonal(getDiagonalPos(rightX, midY));

      this.recommendationBorder.moveTo(rightX, segStart);
      this.recommendationBorder.lineTo(rightX, segEnd);
      this.recommendationBorder.stroke({ color, width: NEON_BORDER_WIDTH, alpha: NEON_GLOW_ALPHA });
    }

    // CORNERS - draw arcs with gradient colors
    const cornerSegments = 10;

    // Top-left corner (PI to PI*1.5)
    for (let i = 0; i < cornerSegments; i++) {
      const startAngle = Math.PI + (i * Math.PI * 0.5) / cornerSegments;
      const endAngle = Math.PI + ((i + 1) * Math.PI * 0.5) / cornerSegments;
      const midAngle = (startAngle + endAngle) / 2;
      const cx = left + radius;
      const cy = top + radius;
      const midX = cx + Math.cos(midAngle) * radius;
      const midY = cy + Math.sin(midAngle) * radius;
      const color = getColorAtDiagonal(getDiagonalPos(midX, midY));

      this.recommendationBorder.arc(cx, cy, radius, startAngle, endAngle);
      this.recommendationBorder.stroke({ color, width: NEON_BORDER_WIDTH, alpha: NEON_GLOW_ALPHA });
    }

    // Top-right corner (PI*1.5 to 2PI)
    for (let i = 0; i < cornerSegments; i++) {
      const startAngle = Math.PI * 1.5 + (i * Math.PI * 0.5) / cornerSegments;
      const endAngle = Math.PI * 1.5 + ((i + 1) * Math.PI * 0.5) / cornerSegments;
      const midAngle = (startAngle + endAngle) / 2;
      const cx = left + width - radius;
      const cy = top + radius;
      const midX = cx + Math.cos(midAngle) * radius;
      const midY = cy + Math.sin(midAngle) * radius;
      const color = getColorAtDiagonal(getDiagonalPos(midX, midY));

      this.recommendationBorder.arc(cx, cy, radius, startAngle, endAngle);
      this.recommendationBorder.stroke({ color, width: NEON_BORDER_WIDTH, alpha: NEON_GLOW_ALPHA });
    }

    // Bottom-right corner (0 to PI*0.5)
    for (let i = 0; i < cornerSegments; i++) {
      const startAngle = (i * Math.PI * 0.5) / cornerSegments;
      const endAngle = ((i + 1) * Math.PI * 0.5) / cornerSegments;
      const midAngle = (startAngle + endAngle) / 2;
      const cx = left + width - radius;
      const cy = top + height - radius;
      const midX = cx + Math.cos(midAngle) * radius;
      const midY = cy + Math.sin(midAngle) * radius;
      const color = getColorAtDiagonal(getDiagonalPos(midX, midY));

      this.recommendationBorder.arc(cx, cy, radius, startAngle, endAngle);
      this.recommendationBorder.stroke({ color, width: NEON_BORDER_WIDTH, alpha: NEON_GLOW_ALPHA });
    }

    // Bottom-left corner (PI*0.5 to PI)
    for (let i = 0; i < cornerSegments; i++) {
      const startAngle = Math.PI * 0.5 + (i * Math.PI * 0.5) / cornerSegments;
      const endAngle = Math.PI * 0.5 + ((i + 1) * Math.PI * 0.5) / cornerSegments;
      const midAngle = (startAngle + endAngle) / 2;
      const cx = left + radius;
      const cy = top + height - radius;
      const midX = cx + Math.cos(midAngle) * radius;
      const midY = cy + Math.sin(midAngle) * radius;
      const color = getColorAtDiagonal(getDiagonalPos(midX, midY));

      this.recommendationBorder.arc(cx, cy, radius, startAngle, endAngle);
      this.recommendationBorder.stroke({ color, width: NEON_BORDER_WIDTH, alpha: NEON_GLOW_ALPHA });
    }
  }

  /**
   * Update countdown arc (called every frame)
   */
  private updateCountdown(): void {
    // Safety check - if graphics are destroyed, skip update
    if (!this.countdownArc || this.countdownArc.destroyed) {
      return;
    }

    if (!this.isVisible || !this.countdownDeadline) {
      this.countdownArc.clear();
      return;
    }

    const now = Date.now();
    const remaining = Math.max(0, this.countdownDeadline - now);
    const progress = remaining / this.countdownTotal;

    if (remaining <= 0) {
      this.countdownArc.clear();
      return;
    }

    // Determine color based on time remaining
    let color = COUNTDOWN_GREEN;
    if (remaining < 3000) {
      color = COUNTDOWN_RED;
    } else if (remaining < 6000) {
      color = COUNTDOWN_YELLOW;
    }

    // Draw countdown arc
    const radius = 20;
    const startAngle = -Math.PI / 2; // Start from top
    const endAngle = startAngle + (2 * Math.PI * progress);

    this.countdownArc.clear();

    // Background circle
    this.countdownArc.circle(0, 0, radius);
    this.countdownArc.stroke({ color: 0x333333, width: 4 });

    // Progress arc
    if (progress > 0) {
      this.countdownArc.arc(0, 0, radius, startAngle, endAngle);
      this.countdownArc.stroke({ color, width: 4 });
    }

    // Time text in center
    const seconds = Math.ceil(remaining / 1000);
    // Note: Could add text here but keeping it simple for now
  }

  /**
   * Cleanup - call when removing from scene
   */
  public dispose(): void {
    // Stop ticker
    if (this.ticker) {
      this.ticker.stop();
      this.ticker.destroy();
      this.ticker = null;
    }

    // Destroy graphics
    this.background.destroy();
    this.titleText.destroy();
    this.costText.destroy();
    this.countdownArc.destroy();
    this.yesButton.graphics.destroy();
    this.yesButton.text.destroy();
    this.noButton.graphics.destroy();
    this.noButton.text.destroy();
    this.recommendationBorder?.destroy();

    this.destroy();
  }
}
